import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import {
    Users, BookOpen, FileBadge, TrendingUp, Award, Sparkles, GraduationCap, Gamepad2, Star, ArrowUpRight,
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend,
} from 'recharts';
import DrillSheet from '@/components/DrillSheet';

const PALETTE = ['#FFF200', '#E31E24', '#F47E1F', '#10B981', '#8B5CF6'];

export default function AdminDashboard() {
    const [kpis, setKpis] = useState(null);
    const [charts, setCharts] = useState(null);
    const [filters, setFilters] = useState({ departments: [], regions: [], roles: [], courses: [], games: [] });
    const [drill, setDrill] = useState(null);

    useEffect(() => {
        api.get('/admin/dashboard/kpis').then((r) => setKpis(r.data));
        api.get('/admin/dashboard/charts').then((r) => setCharts(r.data));
        api.get('/admin/filters/options').then((r) => setFilters(r.data));
    }, []);

    const opt = (arr) => (arr || []).map((v) => ({ value: v, label: v }));

    const DRILLS = {
        learners: {
            title: 'All Learners',
            subtitle: 'Filter by department, region, role and activity status',
            endpoint: '/admin/drill/learners',
            filters: [
                { key: 'department', label: 'department', type: 'select', options: opt(filters.departments) },
                { key: 'region', label: 'region', type: 'select', options: opt(filters.regions) },
                { key: 'role', label: 'role', type: 'select', options: opt(filters.roles) },
                { key: 'status', label: 'status', type: 'select', options: [{ value: 'active', label: 'active' }, { value: 'inactive', label: 'inactive' }] },
            ],
            columns: [
                { key: 'full_name', label: 'Name' },
                { key: 'email', label: 'Email' },
                { key: 'role', label: 'Role' },
                { key: 'department', label: 'Department' },
                { key: 'region', label: 'Region' },
                { key: 'points', label: 'Points', align: 'right' },
            ],
            rowKey: 'id',
            file: 'learners.csv',
        },
        active: {
            title: 'Active Learners',
            subtitle: 'Learners with at least one in-progress or completed course',
            endpoint: '/admin/drill/learners',
            initial: { status: 'active' },
            filters: [
                { key: 'department', label: 'department', type: 'select', options: opt(filters.departments) },
                { key: 'region', label: 'region', type: 'select', options: opt(filters.regions) },
            ],
            columns: [
                { key: 'full_name', label: 'Name' }, { key: 'email', label: 'Email' },
                { key: 'department', label: 'Department' }, { key: 'region', label: 'Region' },
                { key: 'points', label: 'Points', align: 'right' },
            ],
            file: 'active-learners.csv',
        },
        enrollments: {
            title: 'All Enrollments',
            subtitle: 'Cross-tab of learner × course × status',
            endpoint: '/admin/drill/enrollments',
            filters: [
                { key: 'status', label: 'status', type: 'select', options: opt(['not_started', 'in_progress', 'completed', 'certified']) },
                { key: 'course_id', label: 'course', type: 'select', options: (filters.courses || []).map((c) => ({ value: c.id, label: c.title })) },
                { key: 'department', label: 'department', type: 'select', options: opt(filters.departments) },
                { key: 'region', label: 'region', type: 'select', options: opt(filters.regions) },
            ],
            columns: [
                { key: 'user_name', label: 'Learner' },
                { key: 'department', label: 'Dept' },
                { key: 'course_title', label: 'Course' },
                { key: 'status', label: 'Status' },
                { key: 'progress_percent', label: 'Progress %', align: 'right', render: (r) => `${r.progress_percent || 0}%` },
            ],
            rowKey: 'enrollment_id',
            file: 'enrollments.csv',
        },
        certificates: {
            title: 'Certificates Issued',
            subtitle: 'All verifiable certificates awarded across the academy',
            endpoint: '/admin/drill/certificates',
            filters: [
                { key: 'department', label: 'department', type: 'select', options: opt(filters.departments) },
                { key: 'region', label: 'region', type: 'select', options: opt(filters.regions) },
                { key: 'course_id', label: 'course', type: 'select', options: (filters.courses || []).map((c) => ({ value: c.id, label: c.title })) },
            ],
            columns: [
                { key: 'certificate_id', label: 'Cert ID' },
                { key: 'user_name', label: 'Learner' },
                { key: 'department', label: 'Dept' },
                { key: 'course_title', label: 'Course' },
                { key: 'score', label: 'Score', align: 'right', render: (r) => `${Math.round(r.score || 0)}%` },
                { key: 'issued_at', label: 'Issued', render: (r) => new Date(r.issued_at).toLocaleDateString() },
            ],
            rowKey: 'certificate_id',
            file: 'certificates.csv',
        },
        games: {
            title: 'Game Sessions',
            subtitle: 'Arcade play history',
            endpoint: '/admin/drill/games',
            filters: [
                { key: 'game_code', label: 'game', type: 'select', options: (filters.games || []).map((g) => ({ value: g.code, label: g.name })) },
                { key: 'days', label: 'last days', type: 'select', options: [{ value: '7', label: 'last 7 days' }, { value: '30', label: 'last 30 days' }, { value: '90', label: 'last 90 days' }] },
            ],
            columns: [
                { key: 'user_name', label: 'Player' },
                { key: 'game_code', label: 'Game' },
                { key: 'score', label: 'Score', align: 'right' },
                { key: 'points_earned', label: 'Points', align: 'right' },
                { key: 'played_at', label: 'When', render: (r) => new Date(r.played_at).toLocaleString() },
            ],
            file: 'game-sessions.csv',
        },
    };

    const openDrill = (key) => setDrill(DRILLS[key]);

    return (
        <div data-testid="admin-dashboard-page">
            <div className="mb-7">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Command Center</div>
                <h1 className="font-display font-extrabold text-3xl lg:text-4xl text-slate-900 tracking-tight mt-1">
                    Propulse Academy at a glance.
                </h1>
                <p className="text-slate-500 mt-2 text-sm">Click any KPI to drill into the underlying records with filters.</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                <Kpi icon={Users} label="Total Learners" value={kpis?.total_learners ?? '–'} color="bg-brand-black text-brand-yellow" onClick={() => openDrill('learners')} testid="kpi-learners" />
                <Kpi icon={TrendingUp} label="Active Learners" value={kpis?.active_learners ?? '–'} color="bg-emerald-600 text-white" onClick={() => openDrill('active')} testid="kpi-active" />
                <Kpi icon={BookOpen} label="Published Courses" value={`${kpis?.published_courses ?? '–'} / ${kpis?.total_courses ?? '–'}`} color="bg-brand-red text-white" onClick={() => openDrill('enrollments')} testid="kpi-courses" />
                <Kpi icon={GraduationCap} label="Enrollments" value={kpis?.total_enrollments ?? '–'} color="bg-brand-orange text-white" onClick={() => openDrill('enrollments')} testid="kpi-enrollments" />
                <Kpi icon={FileBadge} label="Certificates" value={kpis?.certificates_issued ?? '–'} color="bg-violet-600 text-white" onClick={() => openDrill('certificates')} testid="kpi-certs" />
                <Kpi icon={Award} label="Completion %" value={`${kpis?.completion_pct ?? 0}%`} color="bg-amber-500 text-white" onClick={() => openDrill('enrollments')} testid="kpi-completion" />
                <Kpi icon={Sparkles} label="Points Issued" value={kpis?.total_points_issued ?? '–'} color="bg-rose-600 text-white" onClick={() => openDrill('learners')} testid="kpi-points" />
                <Kpi icon={Gamepad2} label="Game Sessions" value={kpis?.game_sessions ?? '–'} color="bg-slate-800 text-white" onClick={() => openDrill('games')} testid="kpi-games" />
            </div>

            <div className="grid lg:grid-cols-3 gap-5 mt-7">
                <Panel title="Completion by Category" className="lg:col-span-2" testid="chart-by-category">
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={charts?.by_category || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="category" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="total" fill="#231F20" radius={[8, 8, 0, 0]} name="Enrollments" />
                            <Bar dataKey="completed" fill="#FFF200" stroke="#231F20" radius={[8, 8, 0, 0]} name="Completed" />
                        </BarChart>
                    </ResponsiveContainer>
                </Panel>
                <Panel title="Game Engagement" testid="chart-game-engagement">
                    {charts?.game_engagement?.length ? (
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie
                                    data={charts.game_engagement}
                                    dataKey="plays"
                                    nameKey="game"
                                    cx="50%" cy="50%" outerRadius={80} innerRadius={45}
                                    paddingAngle={3}
                                >
                                    {charts.game_engagement.map((_, i) => (
                                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} stroke="#231F20" />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <EmptyChart label="No game plays yet — encourage learners to enter the Arcade." />
                    )}
                </Panel>
            </div>

            <div className="grid lg:grid-cols-2 gap-5 mt-5">
                <Panel title="Top Learners" testid="chart-top-learners" action={<button onClick={() => openDrill('learners')} className="text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="view-all-learners">View all <ArrowUpRight className="h-3 w-3" /></button>}>
                    <div className="divide-y divide-slate-100">
                        {(charts?.top_learners || []).map((u, i) => (
                            <div key={u.id} className="py-2.5 flex items-center gap-3" data-testid={`top-learner-${i}`}>
                                <div className="h-9 w-9 rounded-xl bg-slate-100 flex items-center justify-center font-display font-extrabold text-slate-700">
                                    #{i + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-slate-900 truncate">{u.full_name}</div>
                                    <div className="text-xs text-slate-500 truncate">{u.department || '—'} · {u.region || '—'}</div>
                                </div>
                                <div className="font-display font-extrabold text-slate-900 flex items-center gap-1">
                                    <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" /> {u.points || 0}
                                </div>
                            </div>
                        ))}
                        {!charts?.top_learners?.length && <div className="text-sm text-slate-500 py-6 text-center">No learner activity yet.</div>}
                    </div>
                </Panel>
                <Panel title="Department Performance" testid="chart-departments" action={<button onClick={() => openDrill('enrollments')} className="text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="view-all-depts">Drill <ArrowUpRight className="h-3 w-3" /></button>}>
                    {charts?.department_performance?.length ? (
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={charts.department_performance} layout="vertical" margin={{ left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis type="number" tick={{ fontSize: 11 }} />
                                <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={120} />
                                <Tooltip />
                                <Bar dataKey="avg_progress" fill="#F47E1F" radius={[0, 8, 8, 0]} name="Avg progress %" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : <EmptyChart label="No department data yet." />}
                </Panel>
            </div>

            {drill && (
                <DrillSheet
                    title={drill.title}
                    subtitle={drill.subtitle}
                    endpoint={drill.endpoint}
                    filters={drill.filters}
                    columns={drill.columns}
                    initialFilters={drill.initial || {}}
                    rowKey={drill.rowKey || 'id'}
                    downloadFilename={drill.file || 'export.csv'}
                    onClose={() => setDrill(null)}
                />
            )}
        </div>
    );
}

function Kpi({ icon: Icon, label, value, color, onClick, testid }) {
    return (
        <motion.button
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -2 }}
            onClick={onClick}
            className="text-left bg-white rounded-2xl p-5 border border-slate-200 hover:border-slate-300 hover:shadow-md transition relative group"
            data-testid={testid}
        >
            <div className={`h-9 w-9 rounded-xl ${color} flex items-center justify-center mb-3`}>
                <Icon className="h-4 w-4" />
            </div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
            <div className="font-display font-extrabold text-2xl text-slate-900 mt-1">{value}</div>
            <ArrowUpRight className="absolute top-4 right-4 h-3.5 w-3.5 text-slate-300 group-hover:text-slate-700 transition" />
        </motion.button>
    );
}

function Panel({ title, children, className = '', testid, action }) {
    return (
        <div className={`bg-white rounded-2xl border border-slate-200 p-5 ${className}`} data-testid={testid}>
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-slate-900">{title}</h3>
                {action}
            </div>
            {children}
        </div>
    );
}

function EmptyChart({ label }) {
    return <div className="h-[260px] flex items-center justify-center text-sm text-slate-500 text-center px-6">{label}</div>;
}
